/**
 * Beta banner - displayed at top of app to inform users about beta status.
 * Closeable, but reappears after 7 days.
 */
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import type { BetaMessaging } from "../../types";
import "./BetaBanner.css";

const STORAGE_KEY = "beta-banner-dismissed";
const REAPPEAR_DAYS = 7;

type BetaBannerProps = {
  messaging: BetaMessaging;
};

export function BetaBanner({ messaging }: BetaBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!messaging.is_beta) {
      setVisible(false);
      return;
    }

    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setVisible(true);
      return;
    }

    const dismissedAt = parseInt(dismissed, 10);
    const now = Date.now();
    const daysSince = (now - dismissedAt) / (1000 * 60 * 60 * 24);

    if (daysSince >= REAPPEAR_DAYS) {
      setVisible(true);
    }
  }, [messaging.is_beta]);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
    
    // Track dismissal for analytics (basic client-side event)
    try {
      if (typeof window !== 'undefined' && (window as any).plausible) {
        (window as any).plausible('Beta Banner Dismissed');
      }
    } catch {
      // Ignore analytics errors
    }
  }

  if (!visible) {
    return null;
  }

  return (
    <div className="beta-banner">
      <div className="beta-banner__content">
        <strong>Beta</strong>
        {" — "}
        <span>
          Pełny dostęp za darmo. Planowane płatne plany od{" "}
          {messaging.paid_plans_expected || "Q4 2026"}.
        </span>
        {messaging.grandfather_note && (
          <>
            {" "}
            <span className="beta-banner__benefit">
              {messaging.grandfather_note}
            </span>
          </>
        )}
      </div>
      <button
        className="beta-banner__close"
        onClick={dismiss}
        aria-label="Zamknij banner"
        type="button"
      >
        <X size={16} />
      </button>
    </div>
  );
}
